import { ChangeDetectionStrategy, Component, EventEmitter, Output, HostListener, OnDestroy } from '@angular/core'
import { FormControl } from '@angular/forms'
import { AuthService } from 'src/app/singleton-services/auth/auth.service'
import { ViewAsService, OriginalUserTokens } from 'src/app/singleton-services/view-as.service'
import { BehaviorSubject, of, Subject } from 'rxjs'
import { catchError, takeUntil, take } from 'rxjs/operators'
import { IUserData } from '../../services/user.service'

@Component({
  selector: 'flo-view-as-widget',
  styleUrls: ['./view-as-widget.component.scss'],
  templateUrl: './view-as-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewAsWidgetComponent implements OnDestroy {
  @Output() acknowledged = new EventEmitter<void>()

  // Use service observables directly instead of inputs
  isViewingAsUser$ = this.viewAsService.isViewingAsUser$
  viewedUserInfo$ = this.viewAsService.viewedUserInfo$

  private expanded$ = new BehaviorSubject<boolean>(false)
  private hasAcknowledgedWarning$ = new BehaviorSubject<boolean>(false)

  // Public observables for template
  readonly isExpanded$ = this.expanded$.asObservable()
  readonly hasAcknowledged$ = this.hasAcknowledgedWarning$.asObservable()
  viewAsUserControl = new FormControl('')
  viewAsError$ = new BehaviorSubject<string | null>(null)
  private destroy$ = new Subject<void>()

  constructor(private auth: AuthService, private viewAsService: ViewAsService) {}

  closeModal() {
    this.expanded$.next(false)
    this.hasAcknowledgedWarning$.next(false)
    this.viewAsUserControl.setValue('')
    this.viewAsError$.next(null)
  }

  openModal() {
    this.expanded$.next(true)
  }

  toggleModal() {
    this.expanded$.next(!this.expanded$.value)
  }

  onAcknowledgeWarning() {
    this.hasAcknowledgedWarning$.next(true)
  }

  viewAsUser() {
    const userInput = this.viewAsUserControl.value

    if (!userInput || !userInput.trim()) {
      this.viewAsError$.next('Please enter a user email or ID')
      return
    }

    this.viewAsError$.next(null)

    // Save current admin tokens before switching
    const currentJwtToken = this.auth.getTokenFromStore()
    const currentRefreshToken = this.auth.getRefreshTokenFromStore()

    if (currentJwtToken && currentRefreshToken) {
      const tokenData: OriginalUserTokens = {
        jwt_token: currentJwtToken,
        refresh_token: currentRefreshToken,
        saved_at: new Date().toISOString()
      }
      this.viewAsService.saveOriginalUserTokens(tokenData)
    }

    this.auth
      .getUserTokens(userInput.trim())
      .pipe(
        takeUntil(this.destroy$),
        catchError((error: Error) => {
          this.viewAsError$.next(error.message || 'User not found or insufficient permissions')
          // Clear saved tokens if switching fails
          this.viewAsService.clearOriginalUserTokens()
          return of(null)
        })
      )
      .subscribe((userData: IUserData | null) => {
        if (userData?.jwt_token && userData?.refresh_token) {
          // Store viewed user information
          const viewedUser = {
            email: userData.email || userInput.trim(),
            id: userData.id,
            name:
              userData.first_name && userData.last_name
                ? `${userData.first_name} ${userData.last_name}`.trim()
                : undefined
          }
          this.viewAsService.setViewingAsUser(viewedUser)

          // Both tokens are required for proper user switching
          this.auth
            .setToken(userData.jwt_token)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              // Calculate expiry time - default to 30 days from now
              const refreshTokenExpiry = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60

              this.auth
                .setRefreshToken(userData.refresh_token as string, refreshTokenExpiry)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  this.viewAsUserControl.setValue('')
                  this.viewAsError$.next(null)
                  this.closeModal()
                  // Auth service will automatically update user state via cookie change detection
                })
            })
        } else if (userData?.jwt_token && !userData?.refresh_token) {
          this.viewAsError$.next('Refresh token not provided. Cannot switch user context.')
          // Clear saved tokens if switching fails
          this.viewAsService.clearOriginalUserTokens()
        } else {
          this.viewAsError$.next('No JWT token returned. Admin impersonation may not be enabled.')
          // Clear saved tokens if switching fails
          this.viewAsService.clearOriginalUserTokens()
        }
      })
  }

  stopViewingAsUser() {
    const originalTokens = this.viewAsService.getOriginalUserTokens()

    if (!originalTokens) {
      this.viewAsError$.next('No original user session found. Please log in again.')
      return
    }

    // Restore original admin tokens
    this.auth
      .setToken(originalTokens.jwt_token)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Use a default expiration for the refresh token (30 days from now)
        // The actual validation happens server-side
        const refreshTokenExpiry = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60
        this.auth
          .setRefreshToken(originalTokens.refresh_token, refreshTokenExpiry)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            // Clear the view-as state
            this.viewAsService.clearViewingAsUser()
            // Explicitly clear the admin tokens from sessionStorage after successful restoration
            this.viewAsService.clearOriginalUserTokens()
            this.viewAsError$.next(null)
            this.closeModal()
            // Auth service will automatically update user state via cookie change detection
          })
      })
  }

  @HostListener('window:beforeunload')
  handleBeforeUnload(): void {
    // Use service as single source of truth
    let isViewingAsUser = false

    // Get the current service state synchronously
    this.viewAsService.isViewingAsUser$.pipe(take(1)).subscribe(value => {
      isViewingAsUser = value
    })

    if (isViewingAsUser) {
      // Restore admin tokens automatically so admin doesn't get stuck as impersonated user
      const originalTokens = this.viewAsService.getOriginalUserTokens()

      if (originalTokens) {
        // Use synchronous token setting to restore admin session
        // This prevents admin from getting stuck as impersonated user
        try {
          // Use public AuthService methods with synchronous handling
          this.auth
            .setToken(originalTokens.jwt_token)
            .pipe(take(1))
            .subscribe(() => {
              // For emergency restoration, use reasonable default - AuthService will handle refresh if needed
              const defaultRefreshExpiry = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60
              this.auth
                .setRefreshToken(originalTokens.refresh_token, defaultRefreshExpiry)
                .pipe(take(1))
                .subscribe(() => {
                  // Clear the view-as state from sessionStorage
                  this.viewAsService.clearViewingAsUser()
                })
            })
        } catch (error) {
          // Fallback: at minimum clear the view-as state
          this.viewAsService.clearViewingAsUser()
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
